import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import type { FormEvent } from "react";
import useFavorites from "@/hooks/useFavorites";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/category", label: "影视分类" },
  { to: "/news", label: "新闻资讯" },
  { to: "/special", label: "专题合集" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const { favorites } = useFavorites();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !isHome;

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setKeyword("");
    setMenuOpen(false);
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const linkBase = "text-sm transition-colors duration-200 whitespace-nowrap";
  const linkColor = solid
    ? "text-foreground-600 hover:text-primary-600"
    : "text-white/85 hover:text-white";
  const activeColor = solid ? "text-primary-600" : "text-white";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-6 px-4 md:h-20 md:px-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50 text-lg">
            <i className="ri-flower-fill" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-bold tracking-wide ${
                solid ? "text-foreground-950" : "text-white"
              }`}
            >
              百合影视
            </span>
            <span
              className={`mt-0.5 text-[10px] tracking-[0.2em] ${
                solid ? "text-foreground-400" : "text-white/70"
              }`}
            >
              LILIUM CINEMA
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 ml-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? `${activeColor} font-semibold` : linkColor}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <form onSubmit={submitSearch} className="hidden md:flex">
            <div
              className={`flex h-9 items-center gap-2 rounded-full border px-3 transition-colors ${
                solid
                  ? "border-background-300/70 bg-background-100/70 focus-within:border-primary-300"
                  : "border-white/30 bg-white/10 focus-within:border-white/60"
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center text-sm">
                <i
                  className={`ri-search-line ${
                    solid ? "text-foreground-400" : "text-white/70"
                  }`}
                />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视 / 资讯"
                className={`w-32 lg:w-40 bg-transparent text-sm outline-none ${
                  solid
                    ? "text-foreground-900 placeholder:text-foreground-400"
                    : "text-white placeholder:text-white/60"
                }`}
              />
            </div>
          </form>

          <Link
            to="/category?fav=1"
            aria-label="我的收藏"
            className={`relative hidden md:flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
              solid
                ? "border-background-300/70 text-foreground-600 hover:text-accent-600 hover:border-accent-300"
                : "border-white/30 text-white/85 hover:text-white"
            }`}
          >
            <i className="ri-heart-3-line" />
            {favorites.length > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-semibold text-background-50">
                {favorites.length}
              </span>
            )}
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className={`md:hidden flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
              solid
                ? "border-background-300/70 text-foreground-700"
                : "border-white/30 text-white"
            }`}
          >
            <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 px-4 py-4">
          <form onSubmit={submitSearch} className="mb-3">
            <div className="flex h-10 items-center gap-2 rounded-full border border-background-300/70 bg-background-100/70 px-3">
              <span className="flex h-4 w-4 items-center justify-center text-sm text-foreground-400">
                <i className="ri-search-line" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视 / 资讯"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `border-b border-background-200/60 py-3 text-sm ${
                    isActive
                      ? "text-primary-600 font-semibold"
                      : "text-foreground-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/category?fav=1"
              className="flex items-center justify-between py-3 text-sm text-foreground-700"
            >
              我的收藏
              <span className="text-xs text-foreground-400">
                {favorites.length} 部
              </span>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}